import { useEffect, useState } from "react";

export interface DropResult {
  files: File[];
  /** rejected because they were folders (M9 doesn't recurse) */
  rejectedDirectories: string[];
}

/**
 * Window-level drag detection. `onDrop` fires when the user releases
 * a drag that contains files. The hook returns `isDragging` so the
 * UI can render the drop-overlay.
 */
export function useDragDrop(onDrop: (r: DropResult) => void): {
  isDragging: boolean;
} {
  const [isDragging, setDragging] = useState(false);

  useEffect(() => {
    let depth = 0;

    const hasFiles = (e: DragEvent): boolean =>
      !!e.dataTransfer?.types?.includes("Files");

    const onEnter = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth += 1;
      setDragging(true);
    };
    const onOver = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
    };
    const onLeave = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      depth -= 1;
      if (depth <= 0) {
        depth = 0;
        setDragging(false);
      }
    };
    const onDropEvt = (e: DragEvent): void => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      depth = 0;
      setDragging(false);
      const files: File[] = [];
      const rejectedDirectories: string[] = [];
      const dt = e.dataTransfer!;
      if (dt.items) {
        for (let i = 0; i < dt.items.length; i++) {
          const item = dt.items[i];
          const entry = item.webkitGetAsEntry?.();
          if (entry && entry.isDirectory) {
            rejectedDirectories.push(entry.name);
          } else {
            const f = item.getAsFile();
            if (f) files.push(f);
          }
        }
      } else if (dt.files) {
        for (let i = 0; i < dt.files.length; i++) files.push(dt.files[i]);
      }
      onDrop({ files, rejectedDirectories });
    };

    window.addEventListener("dragenter", onEnter);
    window.addEventListener("dragover", onOver);
    window.addEventListener("dragleave", onLeave);
    window.addEventListener("drop", onDropEvt);
    return () => {
      window.removeEventListener("dragenter", onEnter);
      window.removeEventListener("dragover", onOver);
      window.removeEventListener("dragleave", onLeave);
      window.removeEventListener("drop", onDropEvt);
    };
  }, [onDrop]);

  return { isDragging };
}
